import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowDown, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { scrollToSection } from "./Navbar";
import { useLang } from "../context/LangContext";

const HERO_IMAGES = [
  "/images/hero-engineers.jpg",
  "/images/hero-1.jpg",
  "/images/hero-2.jpg",
  "/images/hero-3.jpg",
  "/images/hero-5.jpg",
  "/images/hero-6.jpg",
];
const HERO_INTERVAL_MS = 5000;

const CountUp = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} dir="ltr" className="font-accent font-bold text-4xl sm:text-5xl text-slate-50">
      {val}
      <span className="text-emerald-400">{suffix}</span>
    </span>
  );
};

export default function Hero() {
  const { lang, t } = useLang();
  const Chevron = lang === "ar" ? ChevronLeft : ChevronRight;
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const im = new Image();
      im.src = src;
    });
    const id = setInterval(
      () => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length),
      HERO_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={i === 0 ? t.hero.imgAlt : ""}
            aria-hidden={i !== 0}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out ${
              i === heroIdx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#14100A]/85 via-[#14100A]/70 to-[#FBF6E9]" />
      <div className="absolute inset-0 blueprint-grid opacity-60" />
      <div className="absolute inset-x-0 top-0 h-[60vh] solar-glow" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-32 pb-20 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="inline-flex items-center gap-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 mb-8"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-accent text-xs sm:text-sm text-emerald-300 tracking-wide">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.55] tracking-tight text-slate-50">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-2">
              <motion.span
                className={`block ${i === 2 ? "text-emerald-400" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToSection("contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 font-bold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_32px_rgba(16,185,129,0.45)]"
          >
            {t.hero.ctaContact}
            <Chevron className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-projects"
            onClick={() => scrollToSection("projects")}
            className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-7 py-3.5 font-semibold text-slate-200 transition-all duration-300 hover:border-emerald-500 hover:text-emerald-400"
          >
            {t.hero.ctaProjects}
          </button>
          <a
            data-testid="hero-cta-profile"
            href="/fikrat-baghdad-profile-2026.pdf"
            download
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-7 py-3.5 font-semibold text-emerald-300 transition-all duration-300 hover:bg-emerald-500 hover:text-white"
          >
            <Download className="w-5 h-5" />
            {t.hero.ctaProfile}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-line pt-8"
          data-testid="hero-metrics"
        >
          {t.hero.metrics.map((m, i) => (
            <div key={i} data-testid={`hero-metric-${i}`}>
              <CountUp to={m.value} suffix={m.suffix} />
              <p className="mt-2 text-xs sm:text-sm text-slate-400">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("services")}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate-400 hover:text-emerald-400 transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        aria-label={t.hero.scroll}
      >
        <ArrowDown className="w-6 h-6" />
      </motion.button>
    </section>
  );
}
